import React, { useState } from 'react';
import { X, Calendar, ListChecks, Plus, Info, Lock } from 'lucide-react';
import { Resource } from '../../lib/resources';
import { createTask, Task } from '../../lib/tasks';
import { useBusiness } from '../../contexts/BusinessContext';
import { Dialog } from '@headlessui/react';
import { CreateChecklistModal } from './CreateChecklistModal';
import { assignChecklistToTask } from '../../lib/checklists';
import { useTasks } from '../../contexts/TasksContext';

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  resources: Resource[];
  onSuccess?: () => Promise<void> | void;
}

type Priority = 'low' | 'medium' | 'high';
type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

export default function CreateTaskModal({ isOpen, onClose, resources, onSuccess }: CreateTaskModalProps) {
  const { selectedBusiness } = useBusiness();
  const { loadTasks, addTask } = useTasks();
  const [title, setTitle] = useState('New Task');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('low');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [isSettingDueDate, setIsSettingDueDate] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    // Format the date in local timezone for datetime-local input
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  });
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<RecurrenceFrequency>('weekly');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [createdTaskId, setCreatedTaskId] = useState<string | null>(null);
  const [pendingChecklist, setPendingChecklist] = useState<{ id: string; name: string } | null>(null);
  const [isConstraintModalOpen, setIsConstraintModalOpen] = useState(false);
  const [constraints, setConstraints] = useState<{
    disableClockOut: boolean;
  }>({
    disableClockOut: false
  });
  const [showTooltip, setShowTooltip] = useState(false);

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'bg-green-100 text-green-800 hover:bg-green-200' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' },
    { value: 'high', label: 'High', color: 'bg-red-100 text-red-800 hover:bg-red-200' },
  ] as const;

  const frequencyOptions: { value: RecurrenceFrequency; label: string }[] = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
  ];

  const activeResources = resources.filter(resource => resource.status === 'active');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBusiness?.id) return;

    try {
      setIsSubmitting(true);
      
      const taskData = {
        title: title.trim(),
        description: description.trim() || undefined,
        business_id: selectedBusiness.id,
        priority,
        resource_id: assignedTo || undefined,
        start_date: startDate,
        due_date: dueDate || undefined,
        is_recurring: isRecurring,
        recurrence_frequency: isRecurring ? recurrenceFrequency : undefined,
        recurrence_start_date: isRecurring ? startDate : undefined,
        recurrence_end_date: undefined,
        constraints: {
          disableClockOut: constraints.disableClockOut
        }
      };

      console.log('Creating task with data:', taskData);
      const task = await createTask(taskData);
      console.log('Task created:', task);

      setCreatedTaskId(task.id);

      // If we have a pending checklist, assign it to the task
      if (pendingChecklist) {
        console.log('Assigning checklist to task:', pendingChecklist);
        try {
          await assignChecklistToTask(task.id, pendingChecklist.id);
          console.log('Checklist assigned successfully');
        } catch (error) {
          console.error('Failed to assign checklist:', error);
          throw error;
        }
      }

      // Immediately add the task to the context
      addTask(task);

      if (!showChecklistModal) {
        onClose();
      }

      if (onSuccess) {
        await onSuccess();
      }
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDueDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDueDate(value);
    if (!value) {
      setIsSettingDueDate(false);
    }
  };

  const handleCreateChecklist = () => {
    setShowChecklistModal(true);
  };

  const handleChecklistCreated = async (checklist: { id: string; name: string }) => {
    console.log('Checklist created:', checklist);
    setPendingChecklist(checklist);
    setShowChecklistModal(false);

    // If we already have a task ID, assign the checklist immediately
    if (createdTaskId) {
      try {
        console.log('Assigning checklist to existing task:', createdTaskId);
        await assignChecklistToTask(createdTaskId, checklist.id);
        console.log('Checklist assigned successfully');
      } catch (error) {
        console.error('Failed to assign checklist:', error);
        alert('Failed to assign checklist. Please try again.');
      }
    }
  };

  const getConstraintsSummary = () => {
    const activeConstraints = [];
    if (constraints.disableClockOut) {
      activeConstraints.push({
        name: 'Disable clock out',
        icon: Lock,
        color: 'text-red-500'
      });
    }
    return activeConstraints;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90]">
        <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Create New Task</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Title
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Enter task title"
                required
                minLength={1}
              />
            </div>
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="description"
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Enter task description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Assign Task
              </label>
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="">Select a resource</option>
                {activeResources.map((resource) => (
                  <option key={resource.id} value={resource.id}>
                    {resource.first_name} {resource.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowChecklistModal(true)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <ListChecks className="h-5 w-5" />
                {pendingChecklist ? `Checklist: ${pendingChecklist.name}` : 'Assign Checklist'}
              </button>
              {pendingChecklist && (
                <button
                  type="button"
                  onClick={() => setPendingChecklist(null)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <div className="flex space-x-2">
                {priorityOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setPriority(option.value)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                      option.color
                    } ${
                      priority === option.value 
                        ? 'ring-2 ring-offset-2 ring-blue-500' 
                        : ''
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">
                Start Date
              </label>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                required
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">
                Due Date
              </label>
              {!isSettingDueDate ? (
                <button
                  type="button"
                  onClick={() => setIsSettingDueDate(true)}
                  className="mt-1 inline-flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  No due date
                </button>
              ) : (
                <div className="mt-1 relative">
                  <input
                    type="datetime-local"
                    value={dueDate}
                    onChange={handleDueDateChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pr-8"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setDueDate('');
                      setIsSettingDueDate(false);
                    }}
                    className="absolute right-2 top-2 text-gray-400 hover:text-gray-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="isRecurring"
                  type="checkbox"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isRecurring" className="ml-2 block text-sm font-medium text-gray-700">
                  Recurring Task
                </label>
              </div>
              <div className="flex items-center gap-2">
                {getConstraintsSummary().map((constraint, index) => {
                  const IconComponent = constraint.icon;
                  return (
                    <div
                      key={index}
                      className="flex items-center bg-gray-100 px-2 py-1 rounded-md"
                      title={constraint.name}
                    >
                      <IconComponent className={`h-4 w-4 ${constraint.color}`} />
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setIsConstraintModalOpen(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Add Constraint
                </button>
              </div>
              </div>
              {isRecurring && (
                <div className="mt-2">
                  <label htmlFor="frequency" className="block text-sm font-medium text-gray-700">
                    Repeat Frequency
                  </label>
                  <select
                    id="frequency"
                    value={recurrenceFrequency}
                    onChange={(e) => setRecurrenceFrequency(e.target.value as RecurrenceFrequency)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    {frequencyOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !title.trim()}
                className={`inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  (isSubmitting || !title.trim()) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Creating...' : 'Create Task'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showChecklistModal && (
        <CreateChecklistModal
          onChecklistCreated={handleChecklistCreated}
          onClose={() => setShowChecklistModal(false)}
        />
      )}

      {isConstraintModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Add Constraint</h3>
              <button
                onClick={() => setIsConstraintModalOpen(false)}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="disableClockOut"
                    checked={constraints.disableClockOut}
                    onChange={(e) => setConstraints(prev => ({
                      ...prev,
                      disableClockOut: e.target.checked
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="disableClockOut" className="text-sm font-medium text-gray-700">
                    Disable clock out
                  </label>
                </div>
                <div className="relative">
                  <button
                    type="button"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    onClick={() => setShowTooltip(!showTooltip)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Info className="h-4 w-4" />
                  </button>
                  {showTooltip && (
                    <div className="absolute right-0 top-6 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-50">
                      A resource cannot clock out while this task is active
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsConstraintModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
} 